import type { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, ReactNode } from "react";

import classnames from "classnames";

import { ProfileDefaultIcon } from "@jti/brand-icons/profile-default";
import { Icon } from "@jti/core-components/components/Icon";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./HeaderToolsAvatar.module.scss";

export type HeaderToolsAvatarVariant = "default" | "initials" | "name";

type HeaderToolsAvatarBaseProps = {
  variant?: HeaderToolsAvatarVariant;
  initials?: string;
  name?: string;
};

export type HeaderToolsAvatarLinkProps = HeaderToolsAvatarBaseProps & {
  href: string;
  nativeBehaviour?: boolean;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

export type HeaderToolsAvatarButtonProps = HeaderToolsAvatarBaseProps & {
  href?: never;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export type HeaderToolsAvatarProps = HeaderToolsAvatarLinkProps | HeaderToolsAvatarButtonProps;

function buildAvatarContent(variant: HeaderToolsAvatarVariant, initials?: string, name?: string): ReactNode {
  if (variant === "initials") {
    return (
      <span className={styles.initialsText} aria-hidden="true">
        {initials}
      </span>
    );
  }
  if (variant === "name") {
    return (
      <>
        <span className={styles.nameCircle} aria-hidden="true">
          {initials}
        </span>
        <span className={styles.nameText}>{name}</span>
      </>
    );
  }
  return (
    <>
      <div className={styles.mobileIcon}>
        <Icon icon={ProfileDefaultIcon} size="sm" />
      </div>
      <div className={styles.desktopIcon}>
        <Icon icon={ProfileDefaultIcon} size="md" />
      </div>
    </>
  );
}

export const HeaderToolsAvatar: FC<HeaderToolsAvatarProps> = (props) => {
  const { variant = "default", initials, name } = props;

  const variantClasses = classnames(styles.avatar, {
    [styles.variantDefault]: variant === "default",
    [styles.variantInitials]: variant === "initials",
    [styles.variantName]: variant === "name",
  });

  const content = buildAvatarContent(variant, initials, name);

  if (typeof props.href === "string") {
    const {
      variant: _variant,
      initials: _initials,
      name: _name,
      className,
      ...linkProps
    } = props as HeaderToolsAvatarLinkProps;

    return (
      <LinkBehaviour {...linkProps} className={classnames(variantClasses, className)}>
        {content}
      </LinkBehaviour>
    );
  }

  const {
    variant: _variant,
    initials: _initials,
    name: _name,
    className,
    type = "button",
    ...buttonProps
  } = props as HeaderToolsAvatarButtonProps;

  return (
    <button {...buttonProps} type={type} className={classnames(variantClasses, className)}>
      {content}
    </button>
  );
};
